import {
  buildPropertyOutliersFromEngagement,
  EXECUTIVE_PORTFOLIO_PROPERTY_COUNT,
} from '../utils/executivePropertyOutliers'
import {
  addCompareSeriesToTrend,
  buildDemandEngagementCompare,
  buildMovementFromProperties,
  buildPropertyBreakdownScopeLabel,
  deriveDemandEngagementKpis,
  getCoveragePercentage,
  hasSignificantPropertyCountChange,
  resolveCoverageStatus,
} from '../components/dashboard/executive/demandEngagementUtils'
import { buildPropertyCompSet } from '../utils/propertyCompSetUtils'
import { buildCompetitiveBenchmarking } from '../components/dashboard/executive/competitiveBenchmarkingUtils'
import { resolveExecutiveClientId } from '../utils/executivePeerGroups'
import {
  EXECUTIVE_CUSTOM_PERIOD_ID,
  formatExecutiveCustomDateRangeLabel,
  resolveExecutivePeriodScale,
  resolveExecutiveTrendPeriod,
} from '../components/dashboard/executive/executivePeriodConfig'
import { formatExecutiveCurrencyMillions } from '../components/dashboard/executive/executiveCurrencyUtils'
import {
  buildCompetitiveBenchmarkingInsights,
  EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE,
} from '../utils/executiveCompetitiveBenchmarking'

/** Portfolio segments from https://www.highgate.com/portfolio/ */
const BRANDS = [
  { id: 'all', label: 'All categories' },
  { id: 'lifestyle-luxury', label: 'Lifestyle & Luxury' },
  { id: 'resort', label: 'Resort' },
  { id: 'full-service', label: 'Full Service' },
  { id: 'select-service', label: 'Select Service' },
]

const PROPERTIES_BY_BRAND = {
  all: [
    { id: 'all', label: 'All properties' },
    { id: 'huntington', label: 'The Huntington Hotel' },
    { id: 'newbury', label: 'The Newbury' },
    { id: 'joule', label: 'The Joule' },
    { id: 'alohilani', label: "'Alohilani Resort" },
    { id: 'paracas', label: 'Hotel Paracas' },
    { id: 'marker', label: 'The Marker' },
    { id: 'westin-lima', label: 'The Westin Lima' },
    { id: 'courtyard-vegas', label: 'Courtyard Las Vegas' },
  ],
  'lifestyle-luxury': [
    { id: 'all', label: 'All properties' },
    { id: 'huntington', label: 'The Huntington Hotel' },
    { id: 'newbury', label: 'The Newbury' },
    { id: 'park-lane', label: 'Park Lane' },
    { id: 'joule', label: 'The Joule' },
    { id: 'crescent', label: 'The Crescent Hotel' },
    { id: 'knickerbocker', label: 'The Knickerbocker' },
  ],
  resort: [
    { id: 'all', label: 'All properties' },
    { id: 'alohilani', label: "'Alohilani Resort" },
    { id: 'paracas', label: 'Hotel Paracas' },
    { id: 'marker', label: 'The Marker' },
    { id: 'tambo-del-inka', label: 'Tambo del Inka' },
    { id: 'cascade', label: 'Cascade Wellness Resort' },
  ],
  'full-service': [
    { id: 'all', label: 'All properties' },
    { id: 'westin-lima', label: 'The Westin Lima' },
    { id: 'ambassador-waikiki', label: 'The Ambassador Hotel of Waikiki' },
    { id: 'candler', label: 'The Candler Hotel' },
    { id: 'renaissance-vegas', label: 'Renaissance Las Vegas' },
  ],
  'select-service': [
    { id: 'all', label: 'All properties' },
    { id: 'courtyard-vegas', label: 'Courtyard Las Vegas' },
    { id: 'home2-longmont', label: 'Home2 Suites Longmont' },
    { id: 'four-points-fw', label: 'Four Points Fort Walton' },
  ],
}

const HOTEL_BRANDS = [
  { id: 'all', label: 'All brands' },
  { id: 'independent', label: 'Independent' },
  { id: 'marriott', label: 'Marriott' },
  { id: 'hilton', label: 'Hilton' },
]

const PROPERTY_HOTEL_BRAND = {
  huntington: 'independent',
  newbury: 'independent',
  'park-lane': 'independent',
  joule: 'independent',
  crescent: 'independent',
  knickerbocker: 'independent',
  alohilani: 'independent',
  paracas: 'marriott',
  marker: 'independent',
  'tambo-del-inka': 'marriott',
  cascade: 'independent',
  'westin-lima': 'marriott',
  'ambassador-waikiki': 'hilton',
  candler: 'hilton',
  'renaissance-vegas': 'marriott',
  'courtyard-vegas': 'marriott',
  'home2-longmont': 'hilton',
  'four-points-fw': 'marriott',
}

const PERIOD_LABELS = {
  '1m': '1M',
  '3m': '3M',
  '6m': '6M',
  ytd: 'YTD',
  [EXECUTIVE_CUSTOM_PERIOD_ID]: 'Custom',
}

const PERIOD_RANGE_LABELS = {
  '1m': 'Last 30 days',
  '3m': 'Last 3 months',
  '6m': 'Last 6 months',
  ytd: 'Year to date',
  [EXECUTIVE_CUSTOM_PERIOD_ID]: 'Custom range',
}

export const EXECUTIVE_KPI_PERIOD_RANGE_LABELS = PERIOD_RANGE_LABELS

const KPI_STATUS = {
  abovePlan: { context: 'Above plan', contextTone: 'positive' },
  onPlan: { context: 'On plan', contextTone: 'neutral' },
  belowPlan: { context: 'Below plan', contextTone: 'negative' },
  onBudget: { context: 'On budget', contextTone: 'neutral' },
  overBudget: { context: 'Over budget', contextTone: 'warning' },
  underBudget: { context: 'Under budget', contextTone: 'neutral' },
  onTarget: { context: 'At target', contextTone: 'positive' },
  aboveTarget: { context: 'Above target', contextTone: 'positive' },
  belowTarget: { context: 'Below target', contextTone: 'warning' },
  improving: { context: 'Improving', contextTone: 'positive' },
  stable: { context: 'Stable', contextTone: 'neutral' },
  declining: { context: 'Declining', contextTone: 'negative' },
  leading: { context: 'Leading', contextTone: 'positive' },
  competitive: { context: 'Competitive', contextTone: 'neutral' },
  lagging: { context: 'Lagging', contextTone: 'warning' },
  aboveSla: { context: 'Above target', contextTone: 'positive' },
  atSla: { context: 'At SLA', contextTone: 'neutral' },
  belowSla: { context: 'Below SLA', contextTone: 'warning' },
}

const leadershipBrief = {
  financial: [
    {
      id: 'influenced-revenue',
      label: 'Revenue',
      value: '$9.46M USD',
      currencyCode: 'USD',
      delta: '+12%',
      deltaDirection: 'up',
      calculation:
        'Total attributed revenue from paid social campaigns running through connected ad accounts for the selected period and scope.',
      dataSource: 'SoHo attribution · PMS booking data',
      ...KPI_STATUS.abovePlan,
    },
    {
      id: 'spend',
      label: 'Spend',
      value: '$3.22M USD',
      currencyCode: 'USD',
      calculation:
        'Total ad spend across all connected paid social campaigns for the selected period and scope.',
      dataSource: 'Meta Ads · Google Ads · TikTok Ads (via SoHo)',
      ...KPI_STATUS.onBudget,
    },
    {
      id: 'roas',
      label: 'ROAS',
      value: '2.9x',
      calculation:
        'Revenue divided by Spend. Measures the efficiency of paid social investment.\n\nFormula: Revenue / Spend = ROAS multiplier (e.g., $9.46M / $3.22M = 2.94x).',
      dataSource: 'Derived from influenced revenue and paid spend feeds',
      ...KPI_STATUS.onTarget,
    },
  ],
  social: [
    {
      id: 'sentiment',
      label: 'Sentiment Score',
      value: '—',
      calculation:
        "The percentage of audience conversations, comments, reviews, and mentions about the property/portfolio that carry positive or neutral sentiment, as classified by SOHO's AI sentiment engine.",
      dataSource: 'SoHo inbox · social listening',
    },
    {
      id: 'share-of-voice',
      label: 'Share of Voice',
      value: '28%',
      calculation:
        "The percentage of total social conversation volume within the property's competitive set that is about the Highgate property/portfolio.",
      dataSource: 'Brandwatch · social mention index',
      ...KPI_STATUS.leading,
    },
    {
      id: 'engagement-rate',
      label: 'Engagement Rate',
      value: '—',
      calculation:
        'Engagement rate measures how actively people interact with social content—engagements (likes, comments, shares, saves) divided by impressions—aggregated across the selected scope.',
      dataSource: 'Instagram · Facebook · TikTok · Google (via SoHo)',
    },
  ],
}

const leadershipBriefDecisions = [
  {
    id: 'guest-response',
    priority: 'act',
    title: 'Guest response coverage is at 42%',
    description:
      'Select Service is the main drag on chain SLA. Staff after-hours coverage before scaling paid on that segment.',
  },
  {
    id: 'roas-plan',
    priority: 'protect',
    title: 'ROAS at 2.9x — above the 2.8x plan',
    description: 'Keep creative quality high on Instagram; avoid broad spend increases that dilute efficiency.',
  },
  {
    id: 'brand-reach',
    priority: 'watch',
    title: 'Lifestyle & Luxury carries 63% of social reach',
    description:
      'Healthy for equity, risky if Full Service/Resort under-invest. Review segment mix in the next planning cycle.',
  },
]

export const EXECUTIVE_ASK_SOHO = {
  placeholder: 'Ask about portfolio performance...',
  emptyHint: 'Ask about revenue, spend, sentiment, or properties that need attention.',
  suggestedQuestions: [
    'Why is International underperforming?',
    'Which hotels need my attention?',
    'Show me the biggest spend anomalies.',
    'How is our AI search visibility changing?',
  ],
  responses: {
    'Why is International underperforming?': {
      summary:
        'International properties are underperforming primarily due to three factors concentrated in a handful of hotels.',
      metrics: [
        { label: 'Revenue', value: '12% below forecast internationally' },
        { label: 'Marketing efficiency', value: 'Spend 8% above plan' },
        { label: 'Reputation', value: 'Sentiment soft at Hotel Paracas' },
      ],
      recommendation:
        'Review Hotel Paracas and The Westin Lima first — together they drive most of the negative variance.',
      followUps: [
        'Show evidence',
        'Compare with last quarter',
        'Which properties are driving this?',
        'What should I ask the regional team?',
      ],
    },
    'Which hotels need my attention?': {
      summary:
        'Three properties need leadership attention this period based on SLA, sentiment, and spend efficiency signals.',
      metrics: [
        { label: 'Park Lane', value: 'Sentiment 62% — 12 pts below portfolio avg' },
        { label: 'Courtyard Las Vegas', value: 'Guest response SLA at 42%' },
        { label: 'The Marker', value: 'ROAS 2.8x with rising spend' },
      ],
      recommendation:
        'Prioritize Park Lane for reputation recovery and Courtyard Las Vegas for inbox staffing before the next paid push.',
      followUps: [
        'Show inbox backlog by property',
        'Compare sentiment trends',
        'Which campaigns are linked?',
      ],
    },
    'Show me the biggest spend anomalies.': {
      summary: 'Spend is pacing 6% above plan, with the largest anomalies in paid social and always-on retargeting.',
      metrics: [
        { label: 'Instagram', value: '+18% vs plan — Lifestyle & Luxury' },
        { label: 'TikTok', value: '+12% vs plan — Resort' },
        { label: 'Facebook', value: '-4% vs plan — Full Service' },
      ],
      recommendation:
        'Audit Lifestyle & Luxury Instagram frequency caps and reallocate TikTok test budget until ROAS stabilizes above 3.0x.',
      followUps: ['Break down by campaign', 'Show ROAS by channel', 'Compare to last month'],
    },
    'How is our AI search visibility changing?': {
      summary:
        'AI search visibility is improving across branded queries but flat on non-branded discovery terms.',
      metrics: [
        { label: 'Branded visibility', value: '+14% vs last quarter' },
        { label: 'Non-branded share', value: 'Flat at 19% of impressions' },
        { label: 'Top mover', value: "'Alohilani Resort +22% citation rate" },
      ],
      recommendation:
        'Expand structured content on spa and wedding pages for properties with the largest non-branded gaps.',
      followUps: ['Show top queries', 'Which pages improved?', 'Compare with competitors'],
    },
  },
}

const BRAND_PROPERTY_UNIVERSE = {
  'lifestyle-luxury': 78,
  resort: 64,
  'full-service': 52,
  'select-service': 48,
}

const HOTEL_BRAND_PROPERTY_UNIVERSE = {
  independent: 140,
  marriott: 72,
  hilton: 30,
}

const PORTFOLIO_PROPERTY_UNIVERSE = Object.values(BRAND_PROPERTY_UNIVERSE).reduce(
  (sum, count) => sum + count,
  0
)

const demandEngagement = {
  demandIndex: {
    value: 113,
    deltaValue: 20,
    deltaDirection: 'up',
  },
  engagementRate: {
    value: '—',
    deltaValue: null,
    deltaDirection: 'neutral',
  },
  trend: [],
}

const DEMAND_TREND_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const getCurrentMonthIndex = (now = new Date()) => now.getMonth()

const getYtdMonthCount = (now = new Date()) => getCurrentMonthIndex(now) + 1

const getRollingMonthLabel = (index, count, now = new Date()) => {
  const end = getCurrentMonthIndex(now)
  const monthIndex = (((end - count + 1 + index) % 12) + 12) % 12
  return DEMAND_TREND_MONTHS[monthIndex]
}

const buildDemandEngagementTrend = (period, periodScale = 1, coverageContext = {}) => {
  const configs = {
    '1m': { count: 4, label: (index) => `Week ${index + 1}` },
    '3m': { count: 12, label: (index) => `Week ${index + 1}` },
    '6m': { count: 6, label: (index) => getRollingMonthLabel(index, 6) },
    ytd: { count: getYtdMonthCount(), label: (index) => DEMAND_TREND_MONTHS[index] || `M${index + 1}` },
  }

  const { count, label } = configs[period] || configs['1m']
  const totalProperties = coverageContext.totalPropertyCount || PORTFOLIO_PROPERTY_UNIVERSE
  const comparablePropertyCount =
    coverageContext.comparablePropertyCount || coverageContext.includedPropertyCount || totalProperties
  const useFixedComparableCohort = coverageContext.useFixedComparableCohort !== false
  const partialIndex = !useFixedComparableCohort && period === '1m' ? 1 : -1

  return Array.from({ length: count }, (_, index) => {
    const progress = count <= 1 ? 1 : index / (count - 1)
    const periodMultiplier = 0.94 + periodScale * 0.06
    const demandStart = Math.round(93 * periodMultiplier)
    const demandEnd = Math.round(113 * periodMultiplier)
    const engagementStart = Number((4.3 * (0.95 + periodScale * 0.04)).toFixed(1))
    const engagementEnd = Number((4.8 * (0.95 + periodScale * 0.04)).toFixed(1))
    const demand = Math.round(demandStart + (demandEnd - demandStart) * progress)
    const engagement = Number(
      (engagementStart + (engagementEnd - engagementStart) * progress).toFixed(1)
    )
    const weeklyPropertyCount =
      partialIndex === index
        ? Math.max(1, Math.round(comparablePropertyCount * 0.88))
        : comparablePropertyCount

    return {
      label: label(index),
      demand,
      engagement,
      propertiesRepresented: weeklyPropertyCount,
      totalProperties,
      cohortDiffers: useFixedComparableCohort
        ? false
        : hasSignificantPropertyCountChange(weeklyPropertyCount, comparablePropertyCount),
    }
  })
}

const getDemandScopeContext = (filters, scopeScale) => {
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const propertyIds = getPropertyIds(filters)
  const propertyLabels = getSelectedPropertyLabels(filters)
  const hasCategoryFilter = !isAllFilterIds(brandIds)
  const hasHotelBrandFilter = !isAllFilterIds(hotelBrandIds)

  if (propertyLabels.length === 1) {
    return {
      mode: 'property',
      propertyName: propertyLabels[0],
      propertiesWithData: 1,
      totalProperties: 1,
      showPortfolioBreadth: false,
      scopeMessage: `Showing Demand Index and Engagement Rate for ${propertyLabels[0]}.`,
    }
  }

  if (!isAllFilterIds(propertyIds) && propertyLabels.length > 1) {
    const totalProperties = propertyLabels.length
    const propertiesWithData = totalProperties
    return {
      mode: 'property-multi',
      propertiesWithData,
      totalProperties,
      showPortfolioBreadth: false,
      scopeMessage: `Showing Demand Index and Engagement Rate for ${propertyLabels.length} selected properties.`,
    }
  }

  if ((hasCategoryFilter || hasHotelBrandFilter) && isAllFilterIds(propertyIds)) {
    const categoryTotal = hasCategoryFilter
      ? brandIds.reduce((sum, brandId) => sum + (BRAND_PROPERTY_UNIVERSE[brandId] || 40), 0)
      : PORTFOLIO_PROPERTY_UNIVERSE
    const hotelBrandTotal = hasHotelBrandFilter
      ? hotelBrandIds.reduce(
          (sum, hotelBrandId) => sum + (HOTEL_BRAND_PROPERTY_UNIVERSE[hotelBrandId] || 40),
          0
        )
      : PORTFOLIO_PROPERTY_UNIVERSE
    const totalProperties =
      hasCategoryFilter && hasHotelBrandFilter
        ? Math.max(8, Math.round((categoryTotal * hotelBrandTotal) / PORTFOLIO_PROPERTY_UNIVERSE))
        : hasHotelBrandFilter
          ? hotelBrandTotal
          : categoryTotal
    const propertiesWithData = Math.min(
      totalProperties,
      Math.round(totalProperties * (brandIds.length + hotelBrandIds.length === 2 ? 0.923 : 0.9))
    )
    const brandName = [
      hasCategoryFilter && formatMultiSelectLabel(brandIds, BRANDS, 'All categories', 'categories'),
      hasHotelBrandFilter &&
        formatMultiSelectLabel(hotelBrandIds, HOTEL_BRANDS, 'All brands', 'brands'),
    ]
      .filter(Boolean)
      .join(' · ')

    return {
      mode: 'brand',
      brandName,
      accountName: 'Highgate',
      propertiesWithData,
      totalProperties,
      includedPropertyCount: propertiesWithData,
      totalPropertyCount: totalProperties,
      comparablePropertyCount: Math.round(propertiesWithData * 0.95),
      useFixedComparableCohort: true,
      showPortfolioBreadth: true,
      brandLine: `Based on ${propertiesWithData} of ${totalProperties} properties within ${brandName}.`,
    }
  }

  const totalProperties = PORTFOLIO_PROPERTY_UNIVERSE
  const coverageRatio = Math.min(1, 0.9 + Math.max(0, scopeScale - 1) * 0.08)
  const propertiesWithData = Math.round(totalProperties * coverageRatio)
  const comparablePropertyCount = Math.round(propertiesWithData * 0.96)

  return {
    mode: 'portfolio',
    accountName: 'Highgate',
    propertiesWithData,
    totalProperties,
    includedPropertyCount: propertiesWithData,
    totalPropertyCount: totalProperties,
    comparablePropertyCount,
    useFixedComparableCohort: true,
    showPortfolioBreadth: true,
  }
}

const buildDemandIndexMovement = (scopeScale) => {
  const scale = Math.max(0.45, Math.min(1.05, scopeScale))

  return {
    improved: Math.round(248 * scale),
    stable: Math.round(79 * scale),
    declined: Math.round(59 * scale),
  }
}

const buildPortfolioCoverage = (scopeContext, { hasChangingWeeklyCohort = false, isStale = false } = {}) => {
  if (scopeContext.mode === 'property' || scopeContext.mode === 'property-multi') {
    return { show: false }
  }

  const includedPropertyCount = scopeContext.includedPropertyCount ?? scopeContext.propertiesWithData
  const totalPropertyCount = scopeContext.totalPropertyCount ?? scopeContext.totalProperties
  const missingCount = Math.max(0, totalPropertyCount - includedPropertyCount)
  const coveragePercentage = getCoveragePercentage(includedPropertyCount, totalPropertyCount)

  return {
    show: true,
    includedPropertyCount,
    totalPropertyCount,
    coveragePercentage,
    status: resolveCoverageStatus({
      includedPropertyCount,
      totalPropertyCount,
      hasChangingWeeklyCohort,
      isStale,
    }),
    detail:
      missingCount > 0
        ? `${missingCount} properties are not included because data is unavailable or incomplete. Missing properties are not counted as zero.`
        : 'All properties in scope have available data for this period.',
  }
}

const EXECUTIVE_PROPERTY_NAME_PARTS = [
  'Harbor',
  'Sunset',
  'Ocean',
  'Palm',
  'Cityline',
  'Riviera',
  'Summit',
  'Cedar',
  'Golden',
  'Silver',
  'Crystal',
  'Aurora',
  'Bay',
  'Heights',
  'Grand',
  'Vista',
  'Inn',
  'Lodge',
  'Resort',
  'Hotel',
  'Suites',
  'Plaza',
  'Terrace',
  'Gardens',
]

const MOVEMENT_LABELS = {
  improved: 'Improved',
  stable: 'Stable',
  declined: 'Declined',
}

const PROPERTY_BRAND_MAP = Object.entries(PROPERTIES_BY_BRAND).reduce((map, [brandId, properties]) => {
  if (brandId === 'all') return map

  const brandLabel = BRANDS.find((brand) => brand.id === brandId)?.label || brandId
  properties.forEach((property) => {
    if (property.id !== 'all') {
      map[property.id] = brandLabel
    }
  })
  return map
}, {})

const hashPropertyId = (propertyId = '') => {
  let hash = 0
  for (let index = 0; index < propertyId.length; index += 1) {
    hash = (hash + propertyId.charCodeAt(index) * (index + 1)) % 997
  }
  return hash
}

const buildSyntheticPropertyName = (index) => {
  const partA = EXECUTIVE_PROPERTY_NAME_PARTS[index % EXECUTIVE_PROPERTY_NAME_PARTS.length]
  const partB =
    EXECUTIVE_PROPERTY_NAME_PARTS[(index * 7 + 3) % EXECUTIVE_PROPERTY_NAME_PARTS.length]
  const partC =
    EXECUTIVE_PROPERTY_NAME_PARTS[(index * 11 + 5) % EXECUTIVE_PROPERTY_NAME_PARTS.length]

  if (index % 5 === 0) return `${partA} ${partB} ${partC}`
  if (index % 3 === 0) return `The ${partA} ${partC}`
  return `${partA} ${partB}`
}

const getBrandUniverseForScope = (filters) => {
  const brandIds = getBrandIds(filters)
  const propertyIds = getPropertyIds(filters)

  if (!isAllFilterIds(brandIds) && isAllFilterIds(propertyIds)) {
    return Object.fromEntries(
      brandIds.map((brandId) => [brandId, BRAND_PROPERTY_UNIVERSE[brandId] || 40])
    )
  }

  return BRAND_PROPERTY_UNIVERSE
}

const buildBrandAssignmentPlan = (count, brandUniverse) => {
  const entries = Object.entries(brandUniverse)
  const universeTotal = entries.reduce((sum, [, brandCount]) => sum + brandCount, 0)
  if (!count || !entries.length || !universeTotal) return []

  const plan = entries.flatMap(([brandId, brandCount]) => {
    const slots = Math.round((brandCount / universeTotal) * count)
    return Array.from({ length: slots }, () => brandId)
  })

  while (plan.length < count) {
    const largestBrand = entries.reduce((current, entry) =>
      entry[1] > (current?.[1] || 0) ? entry : current
    )[0]
    plan.push(largestBrand)
  }

  return plan.slice(0, count)
}

const getNamedPropertiesForScope = (filters) => {
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const propertyIds = getPropertyIds(filters)
  const propertyLabels = getSelectedPropertyLabels(filters)

  if (!isAllFilterIds(propertyIds) && propertyLabels.length) {
    const brandId = brandIds.length === 1 ? brandIds[0] : 'all'
    const propertyOptions = PROPERTIES_BY_BRAND[brandId] || PROPERTIES_BY_BRAND.all

    return propertyIds
      .map((propertyId) => propertyOptions.find((property) => property.id === propertyId))
      .filter(Boolean)
      .map((property) => ({
        id: property.id,
        label: property.label,
        brand: PROPERTY_BRAND_MAP[property.id] || 'Highgate',
      }))
  }

  if (!isAllFilterIds(brandIds) && isAllFilterIds(propertyIds)) {
    const seen = new Set()
    const properties = []

    brandIds.forEach((brandId) => {
      ;(PROPERTIES_BY_BRAND[brandId] || []).forEach((property) => {
        if (property.id === 'all' || seen.has(property.id)) return
        if (!propertyMatchesHotelBrands(property.id, hotelBrandIds)) return
        seen.add(property.id)
        properties.push({
          id: property.id,
          label: property.label,
          brand: PROPERTY_BRAND_MAP[property.id] || 'Highgate',
        })
      })
    })

    return properties
  }

  return PROPERTIES_BY_BRAND.all
    .filter(
      (property) => property.id !== 'all' && propertyMatchesHotelBrands(property.id, hotelBrandIds)
    )
    .map((property) => ({
      id: property.id,
      label: property.label,
      brand: PROPERTY_BRAND_MAP[property.id] || 'Highgate',
    }))
}

const expandPropertiesToSampleSize = (namedProperties, targetCount, filters) => {
  if (!targetCount || targetCount <= 0) return namedProperties
  if (namedProperties.length >= targetCount) return namedProperties.slice(0, targetCount)

  const result = [...namedProperties]
  const remainingCount = targetCount - namedProperties.length
  const brandUniverse = getBrandUniverseForScope(filters)
  const brandPlan = buildBrandAssignmentPlan(remainingCount, brandUniverse)

  brandPlan.forEach((brandId, index) => {
    const brandLabel = BRANDS.find((brand) => brand.id === brandId)?.label || 'Highgate'
    const propertyIndex = result.length

    result.push({
      id: `highgate-${brandId}-${index + 1}`,
      label: buildSyntheticPropertyName(propertyIndex),
      brand: brandLabel,
    })
  })

  return result
}

const getPropertiesForBreakdown = (filters, scopeContext) => {
  const namedProperties = getNamedPropertiesForScope(filters)
  const propertyIds = getPropertyIds(filters)
  const propertyLabels = getSelectedPropertyLabels(filters)

  if (!isAllFilterIds(propertyIds) && propertyLabels.length) {
    return namedProperties
  }

  const targetCount =
    scopeContext?.includedPropertyCount ??
    scopeContext?.propertiesWithData ??
    namedProperties.length

  return expandPropertiesToSampleSize(namedProperties, targetCount, filters)
}

const assignPropertyMovements = (properties, movement) => {
  const totalMovement = movement.improved + movement.stable + movement.declined
  if (!properties.length || !totalMovement) return []

  const improvedCount = Math.max(0, Math.round((movement.improved / totalMovement) * properties.length))
  const stableCount = Math.max(
    0,
    Math.round((movement.stable / totalMovement) * properties.length)
  )
  const declinedCount = Math.max(0, properties.length - improvedCount - stableCount)

  const assignments = [
    ...Array(improvedCount).fill('improved'),
    ...Array(stableCount).fill('stable'),
    ...Array(declinedCount).fill('declined'),
  ]

  return properties
    .map((property, index) => ({
      property,
      sortKey: hashPropertyId(property.id) + index,
      movement: assignments[index] || 'stable',
    }))
    .sort((left, right) => left.sortKey - right.sortKey)
}

const buildPropertyMetricValues = (propertyId, movement, baseDemand, baseEngagement, scopeScale) => {
  const hash = hashPropertyId(propertyId)
  const scale = Math.max(0.85, scopeScale)

  if (movement === 'improved') {
    return {
      demandIndex: Math.round(baseDemand + (8 + (hash % 18)) * scale),
      engagementRate: Number((baseEngagement + 0.3 + (hash % 12) / 10).toFixed(1)),
    }
  }

  if (movement === 'declined') {
    return {
      demandIndex: Math.max(1, Math.round(baseDemand - (10 + (hash % 15)) * scale)),
      engagementRate: Number(Math.max(0.1, baseEngagement - 0.4 - (hash % 10) / 10).toFixed(1)),
    }
  }

  return {
    demandIndex: Math.round(baseDemand + ((hash % 6) - 3) * scale),
    engagementRate: Number((baseEngagement + (hash % 6) / 20 - 0.15).toFixed(1)),
  }
}

const buildPropertyBreakdownRows = (
  filters,
  movement,
  scopeContext,
  { baseDemand, baseEngagement, scopeScale }
) => {
  const properties = getPropertiesForBreakdown(filters, scopeContext)

  return assignPropertyMovements(properties, movement).map(({ property, movement: movementKey }) => {
    const metrics = buildPropertyMetricValues(
      property.id,
      movementKey,
      baseDemand,
      baseEngagement,
      scopeScale
    )

    return {
      id: property.id,
      property: property.label,
      brand: property.brand,
      movement: movementKey,
      movementLabel: MOVEMENT_LABELS[movementKey],
      demandIndex: metrics.demandIndex,
      engagementRate: metrics.engagementRate,
    }
  })
}

const buildDemandEngagement = (filters, periodScale, scopeScale, compareMeta) => {
  const combinedScale = periodScale * Math.max(0.75, scopeScale)
  const scopeContext = getDemandScopeContext(filters, scopeScale)
  const movement = buildDemandIndexMovement(scopeScale)
  const next = clone(demandEngagement)

  next.trend = addCompareSeriesToTrend(
    buildDemandEngagementTrend(
      resolveExecutiveTrendPeriod(filters.period, filters.customDateRange),
      combinedScale,
      scopeContext
    ).map((point) => ({
      ...point,
      demand: Math.round(point.demand * Math.max(0.85, scopeScale)),
      engagement: Number((point.engagement * Math.min(1.12, 0.92 + scopeScale * 0.1)).toFixed(1)),
    })),
    compareMeta
  )

  next.compare = buildDemandEngagementCompare(compareMeta)

  const kpis = deriveDemandEngagementKpis(next.trend, compareMeta)
  next.demandIndex = kpis.demandIndex
  next.engagementRate = kpis.engagementRate

  const first = next.trend[0]
  const latest = next.trend[next.trend.length - 1]

  next.scope = scopeContext

  const hasChangingWeeklyCohort = next.trend.some((point) => point.cohortDiffers)
  const isStale = compareMeta.deltaFactor === 0

  next.portfolioCoverage = buildPortfolioCoverage(scopeContext, { hasChangingWeeklyCohort, isStale })
  next.comparableCohort = scopeContext.showPortfolioBreadth
    ? {
        show: true,
        propertyCount:
          scopeContext.comparablePropertyCount ??
          scopeContext.includedPropertyCount ??
          scopeContext.propertiesWithData,
        usesFixedCohort: scopeContext.useFixedComparableCohort !== false && !hasChangingWeeklyCohort,
      }
    : { show: false }

  const improvedPercent = Math.round(
    (movement.improved / (movement.improved + movement.stable + movement.declined)) * 100
  )
  const breakdownProperties = buildPropertyBreakdownRows(filters, movement, scopeContext, {
    baseDemand: latest?.demand ?? 113,
    baseEngagement: latest?.engagement ?? 4.7,
    scopeScale,
  })
  const demandMovement = buildMovementFromProperties(breakdownProperties)
  const engagementMovement = null

  next.portfolioMovement = scopeContext.showPortfolioBreadth
    ? {
        show: true,
        mode: engagementMovement ? 'dual' : 'drawer-link',
        demand: demandMovement,
        engagement: engagementMovement,
      }
    : { show: false }

  next.propertyBreakdown = scopeContext.showPortfolioBreadth
    ? {
        scopeLabel: buildPropertyBreakdownScopeLabel(scopeContext, breakdownProperties.length),
        properties: breakdownProperties,
      }
    : null

  return next
}


const channelMix = {
  audience: { channels: [] },
  spend: { channels: [] },
  engagement: { channels: [] },
}

const conversationTopics = {
  topics: [],
  positive: [],
  negative: [],
  neutral: [],
  topicCount: 0,
  indexedVolume: 0,
  positiveTopicCount: 0,
  negativeTopicCount: 0,
}

const supportingInsights = {
  propertyOutliers: {
    propertyCount: EXECUTIVE_PORTFOLIO_PROPERTY_COUNT,
    underperforming: [],
    outperforming: [],
  },
  ugcPipeline: {
    stages: [
      { stage: 'Submitted', count: 7533 },
      { stage: 'Approved', count: 5574 },
      { stage: 'Rights cleared', count: 4905 },
      { stage: 'Pushed to DAM', count: 4022 },
    ],
  },
  inboxHealth: {
    metrics: [
      { id: 'messages', label: 'Messages', value: '—' },
      { id: 'median-reply', label: 'Median reply', value: '—' },
      { id: 'sla-under-1h', label: 'SLA under 1h', value: '—' },
      { id: 'outside-hours', label: 'Outside hours', value: '—' },
      { id: 'sentiment-positive', label: 'Sentiment +', value: '—' },
      { id: 'negative-alerts', label: 'Neg. alerts', value: '—' },
    ],
  },
  competitiveBenchmarking: {
    availability: {
      status: 'ready',
      migrationPending: false,
      totalPropertyCount: EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE,
    },
  },
}

export const DEFAULT_EXECUTIVE_FILTERS = {
  period: '1m',
  customDateRange: null,
  brandIds: ['all'],
  hotelBrandIds: ['all'],
  propertyIds: ['all'],
  comparePeriod: 'mom',
}

export const isAllFilterIds = (ids) => !ids?.length || ids.includes('all')

export const normalizeFilterIds = (ids) => {
  const list = typeof ids === 'string' ? [ids] : ids
  if (!Array.isArray(list) || list.length === 0 || list.includes('all')) return ['all']
  return [...new Set(list.filter((id) => id && id !== 'all'))]
}

export const filterIdsEqual = (left, right) => {
  const a = normalizeFilterIds(left)
  const b = normalizeFilterIds(right)
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((value, index) => value === sortedB[index])
}

export const formatMultiSelectLabel = (ids, options, allLabel, pluralNoun) => {
  const normalized = normalizeFilterIds(ids)
  if (isAllFilterIds(normalized)) return allLabel
  const labels = normalized
    .map((id) => options.find((option) => option.id === id)?.label)
    .filter(Boolean)
  if (labels.length === 0) return allLabel
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return labels.join(', ')
  return `${labels.length} ${pluralNoun}`
}

export const toggleMultiSelectId = (currentIds, optionId, allOptionIds) => {
  if (optionId === 'all') return ['all']

  const normalized = normalizeFilterIds(currentIds)
  const selected = isAllFilterIds(normalized) ? new Set() : new Set(normalized)

  if (selected.has(optionId)) selected.delete(optionId)
  else selected.add(optionId)

  if (selected.size === 0) return ['all']
  if (allOptionIds?.length && selected.size >= allOptionIds.length) return ['all']
  return [...selected]
}


const resolveDashboardPeriodMeta = (filters) => {
  const periodScale = resolveExecutivePeriodScale(filters.period, filters.customDateRange)
  const trendPeriod = resolveExecutiveTrendPeriod(filters.period, filters.customDateRange)
  const periodLabel =
    filters.period === EXECUTIVE_CUSTOM_PERIOD_ID
      ? formatExecutiveCustomDateRangeLabel(filters.customDateRange)
      : PERIOD_LABELS[filters.period] || filters.period
  const periodRangeLabel =
    filters.period === EXECUTIVE_CUSTOM_PERIOD_ID
      ? formatExecutiveCustomDateRangeLabel(filters.customDateRange)
      : PERIOD_RANGE_LABELS[filters.period] || PERIOD_RANGE_LABELS['1m']

  return { periodScale, trendPeriod, periodLabel, periodRangeLabel }
}

const formatMillions = formatExecutiveCurrencyMillions

const clone = (value) => JSON.parse(JSON.stringify(value))

const benchmarkSeed = (input) =>
  Array.from(String(input)).reduce((sum, char) => sum + char.charCodeAt(0), 0)

const benchmarkSeeded = (seed) => {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

const BRAND_BENCHMARK_TARGETS = {
  'Lifestyle & Luxury': { shareOfVoice: 26.5, engagementRate: 4.9, followerGrowth: 5.8 },
  Resort: { shareOfVoice: 24.6, engagementRate: 4.4, followerGrowth: 5.5 },
  'Full Service': { shareOfVoice: 22.8, engagementRate: 3.9, followerGrowth: 4.8 },
  'Select Service': { shareOfVoice: 21.4, engagementRate: 3.6, followerGrowth: 4.2 },
}

const buildCompetitiveBenchmarkPropertyRecord = (property, brandLabel, index) => {
  const targets = BRAND_BENCHMARK_TARGETS[brandLabel] || BRAND_BENCHMARK_TARGETS['Full Service']
  const seed = benchmarkSeed(`${property.id}-${brandLabel}-${index}`)
  const variance = benchmarkSeeded(seed)
  const engagementVariance = benchmarkSeeded(seed + 11)
  const followerVariance = benchmarkSeeded(seed + 23)
  const compSet = buildPropertyCompSet(property.id, brandLabel)

  return {
    id: property.id,
    propertyId: property.id,
    name: property.label,
    brand: brandLabel,
    metrics: {
      shareOfVoice: {
        value: Number((targets.shareOfVoice - 1.4 + variance * 4.8).toFixed(1)),
        benchmark: targets.shareOfVoice,
      },
      engagementRate: {
        value: Number((targets.engagementRate - 1.6 + engagementVariance * 3.4).toFixed(1)),
        benchmark: targets.engagementRate,
      },
      followerGrowth: {
        value: Number((targets.followerGrowth - 1.8 + followerVariance * 3.6).toFixed(1)),
        benchmark: targets.followerGrowth,
      },
    },
    competitors: compSet.competitors,
    compSetMeta: compSet.meta,
  }
}

const buildCompetitiveBenchmarkProperties = (filters) => {
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const propertyIds = getPropertyIds(filters)
  const brandKeys = isAllFilterIds(brandIds)
    ? Object.keys(PROPERTIES_BY_BRAND).filter((key) => key !== 'all')
    : brandIds

  const properties = []

  brandKeys.forEach((brandId) => {
    const brandLabel = BRANDS.find((brand) => brand.id === brandId)?.label
    if (!brandLabel) return

    const brandProperties = (PROPERTIES_BY_BRAND[brandId] || []).filter(
      (property) => property.id !== 'all'
    )

    brandProperties.forEach((property, index) => {
      if (!isAllFilterIds(propertyIds) && !propertyIds.includes(property.id)) return
      if (!propertyMatchesHotelBrands(property.id, hotelBrandIds)) return
      properties.push(buildCompetitiveBenchmarkPropertyRecord(property, brandLabel, index))
    })
  })

  return properties
}

const buildCompetitiveBenchmarkingSupportingInsights = (filters, scopeScale, periodBadge) => {
  const representedPropertyCount = Math.min(
    EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE,
    Math.max(
      1,
      Math.round(EXECUTIVE_PORTFOLIO_PROPERTY_COUNT * Math.max(0.58, Math.min(1, scopeScale + 0.08)))
    )
  )
  const coverage = {
    representedPropertyCount,
    totalPropertyCount: EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE,
    statusLabel:
      representedPropertyCount >= EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE
        ? 'Complete coverage'
        : 'Partial coverage',
  }

  const availability = {
    status: 'ready',
    migrationPending: false,
    totalPropertyCount: EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE,
  }

  const properties = buildCompetitiveBenchmarkProperties(filters)

  return buildCompetitiveBenchmarkingInsights({
    properties,
    coverage,
    availability,
    periodBadge,
  })
}

const getBrandIds = (filters) => normalizeFilterIds(filters.brandIds ?? filters.brandId)
const getHotelBrandIds = (filters) =>
  normalizeFilterIds(filters.hotelBrandIds ?? filters.hotelBrandId)
const getPropertyIds = (filters) => normalizeFilterIds(filters.propertyIds ?? filters.propertyId)

const propertyMatchesHotelBrands = (propertyId, hotelBrandIds) =>
  isAllFilterIds(hotelBrandIds) || hotelBrandIds.includes(PROPERTY_HOTEL_BRAND[propertyId])

const getSelectedBrandLabels = (filters) => {
  const brandIds = getBrandIds(filters)
  if (isAllFilterIds(brandIds)) {
    return BRANDS.filter((brand) => brand.id !== 'all').map((brand) => brand.label)
  }
  return brandIds
    .map((id) => BRANDS.find((brand) => brand.id === id)?.label)
    .filter(Boolean)
}

const getSelectedPropertyLabels = (filters) => {
  const propertyIds = getPropertyIds(filters)
  if (isAllFilterIds(propertyIds)) return []
  const propertyLists = Object.values(PROPERTIES_BY_BRAND)
  return propertyIds
    .map((id) => propertyLists.flat().find((property) => property.id === id)?.label)
    .filter(Boolean)
}

const getScopeScale = (filters) => {
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const propertyIds = getPropertyIds(filters)
  let scale = 1
  if (!isAllFilterIds(brandIds)) {
    scale *= Math.max(0.28, brandIds.length / 4)
  }
  if (!isAllFilterIds(hotelBrandIds)) {
    scale *= Math.max(0.28, hotelBrandIds.length / 3)
  }
  if (!isAllFilterIds(propertyIds)) {
    scale *= Math.max(0.32, propertyIds.length / 3)
  }
  return Number(scale.toFixed(3))
}

const getCompareMeta = (comparePeriod) => {
  if (comparePeriod === 'yoy') {
    return { label: 'vs last year', short: 'Same period last year', deltaFactor: 1.15 }
  }
  if (comparePeriod === 'none') {
    return { label: 'current period', short: 'None', deltaFactor: 0 }
  }
  return { label: 'vs previous period', short: 'Previous period', deltaFactor: 1 }
}

const scaleNumericString = (raw, scale, digits = 1) => {
  if (raw == null) return raw
  const text = String(raw).trim()
  const match = text.match(/^([^0-9-+]*)([-+]?\d+(?:\.\d+)?)(.*)$/)
  if (!match) return text
  const [, prefix, numberPart, suffix] = match
  const scaled = Number(numberPart) * scale
  const formatted =
    digits === 0 || Number.isInteger(scaled)
      ? String(Math.round(scaled))
      : scaled.toFixed(digits).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1')
  return `${prefix}${formatted}${suffix}`
}

const scaleDeltaString = (raw, compareMeta) => {
  if (!raw || compareMeta.deltaFactor === 0) return undefined
  return scaleNumericString(raw, compareMeta.deltaFactor, 1)
}

const buildFinancialDataCoverage = (scopeContext, scopeScale) => {
  if (scopeContext.mode === 'property' || scopeContext.mode === 'property-multi') {
    return { show: false }
  }

  const totalPropertyCount = scopeContext.totalPropertyCount ?? scopeContext.totalProperties
  const connectionRatio = Math.min(0.92, 0.76 + Math.max(0, scopeScale - 1) * 0.05)
  const connectedPropertyCount = Math.min(
    totalPropertyCount,
    Math.round(totalPropertyCount * connectionRatio)
  )
  const isComplete = connectedPropertyCount >= totalPropertyCount

  let line
  if (scopeContext.mode === 'portfolio') {
    line = isComplete
      ? `All ${totalPropertyCount} properties connected`
      : `${connectedPropertyCount} of ${totalPropertyCount} properties connected`
  } else {
    line = isComplete
      ? `All ${totalPropertyCount} selected properties connected`
      : `${connectedPropertyCount} of ${totalPropertyCount} selected properties connected`
  }

  return {
    show: true,
    connectedPropertyCount,
    totalPropertyCount,
    isComplete,
    line,
    detail:
      'Revenue, Spend, and ROAS reflect properties with active ad account connections in the selected period. Properties without stable connections are excluded from these metrics.',
  }
}

const buildLeadershipBrief = (filters, periodScale, scopeScale, compareMeta) => {
  const volumeScale = periodScale * scopeScale
  const scopeContext = getDemandScopeContext(filters, scopeScale)
  const next = clone(leadershipBrief)

  next.financial = next.financial.map((metric) => {
    if (metric.id === 'influenced-revenue') {
      const status =
        compareMeta.deltaFactor === 0
          ? KPI_STATUS.onPlan
          : scopeScale < 0.55
            ? KPI_STATUS.belowPlan
            : KPI_STATUS.abovePlan
      return {
        ...metric,
        value: formatMillions(9.46 * volumeScale),
        currencyCode: 'USD',
        delta: scaleDeltaString('+12%', compareMeta),
        deltaDirection:
          compareMeta.deltaFactor === 0 ? undefined : status.contextTone === 'negative' ? 'down' : 'up',
        ...status,
      }
    }
    if (metric.id === 'spend') {
      const status = scopeScale > 1.05 ? KPI_STATUS.overBudget : KPI_STATUS.onBudget
      return {
        ...metric,
        value: formatMillions(3.22 * volumeScale),
        currencyCode: 'USD',
        ...status,
      }
    }
    if (metric.id === 'roas') {
      const roas = Number((2.9 * Math.min(1.12, 0.94 + scopeScale * 0.1 + (periodScale - 1) * 0.05)).toFixed(1))
      const status = roas >= 3.0 ? KPI_STATUS.aboveTarget : roas >= 2.8 ? KPI_STATUS.onTarget : KPI_STATUS.belowTarget
      return {
        ...metric,
        value: `${Math.round(roas)}x`,
        ...status,
      }
    }
    return metric
  })

  next.social = next.social.map((metric) => {
    if (metric.id === 'sentiment') return metric
    if (metric.id === 'share-of-voice') {
      const sov = Math.round(28 * Math.min(1.2, 0.85 + scopeScale * 0.2))
      const status = sov >= 26 ? KPI_STATUS.leading : sov >= 20 ? KPI_STATUS.competitive : KPI_STATUS.lagging
      return {
        ...metric,
        value: `${sov}%`,
        ...status,
      }
    }
    if (metric.id === 'engagement-rate') {
      return {
        ...metric,
        value: '—',
        context: undefined,
        contextTone: undefined,
      }
    }
    return metric
  })

  const timeRange = resolveDashboardPeriodMeta(filters).periodRangeLabel
  next.financial = next.financial.map((metric) => ({ ...metric, timeRange }))
  next.social = next.social.map((metric) => ({ ...metric, timeRange }))
  next.financialDataCoverage = buildFinancialDataCoverage(scopeContext, scopeScale)

  return next
}

const buildChannelMix = () => clone(channelMix)

const buildConversationTopics = () => clone(conversationTopics)

const buildSupportingInsights = (filters, periodScale, scopeScale, compareMeta) => {
  const next = clone(supportingInsights)
  const propertyLabels = getSelectedPropertyLabels(filters)
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const volumeScale = periodScale * scopeScale
  const isFullScope = isAllFilterIds(brandIds) && isAllFilterIds(hotelBrandIds)

  const baseCount = isFullScope
    ? EXECUTIVE_PORTFOLIO_PROPERTY_COUNT
    : Math.max(
        propertyLabels.length || 1,
        brandIds.reduce((count, brandId) => {
          const list = PROPERTIES_BY_BRAND[brandId] || []
          return count + Math.max(list.length - 1, 0)
        }, 0) * (isAllFilterIds(getPropertyIds(filters)) ? 8 : 1)
      )

  next.propertyOutliers.propertyCount = Math.max(
    1,
    Math.round(baseCount * (isFullScope ? 1 : scopeScale * 1.4))
  )

  const scopeContext = getDemandScopeContext(filters, scopeScale)
  const movement = buildDemandIndexMovement(scopeScale)
  const propertyRows = buildPropertyBreakdownRows(filters, movement, scopeContext, {
    baseDemand: 113,
    baseEngagement: 4.7,
    scopeScale,
  })

  const engagementOutliers = buildPropertyOutliersFromEngagement(propertyRows, {
    propertyCount: next.propertyOutliers.propertyCount,
  })

  const scaleOutlierVariance = (item) => ({
    ...item,
    variance: scaleNumericString(
      item.variance,
      compareMeta.deltaFactor === 0 ? 1 : compareMeta.deltaFactor,
      1
    ),
  })

  next.propertyOutliers = {
    ...engagementOutliers,
    underperforming: engagementOutliers.underperforming.map(scaleOutlierVariance),
    outperforming: engagementOutliers.outperforming.map(scaleOutlierVariance),
  }

  next.ugcPipeline.stages = next.ugcPipeline.stages.map((stage) => ({
    ...stage,
    count: Math.max(1, Math.round(stage.count * volumeScale)),
  }))

  next.inboxHealth = {
    metrics: [
      { id: 'messages', label: 'Messages', value: '—' },
      { id: 'median-reply', label: 'Median reply', value: '—' },
      { id: 'sla-under-1h', label: 'SLA under 1h', value: '—' },
      { id: 'outside-hours', label: 'Outside hours', value: '—' },
      { id: 'sentiment-positive', label: 'Sentiment +', value: '—' },
      { id: 'negative-alerts', label: 'Neg. alerts', value: '—' },
    ],
  }

  next.competitiveBenchmarking = buildCompetitiveBenchmarkingSupportingInsights(
    filters,
    scopeScale,
    PERIOD_LABELS[filters.period] || PERIOD_LABELS['1m']
  )

  return next
}

const buildLeadershipBriefDecisions = (filters, periodScale, scopeScale, compareMeta, scopeLabel) => {
  const next = clone(leadershipBriefDecisions)
  const periodLabel = PERIOD_LABELS[filters.period] || filters.period
  return next.map((decision, index) => {
    if (index === 0) {
      const coverage = Math.max(28, Math.round(42 * (0.85 + scopeScale * 0.2)))
      return {
        ...decision,
        title: `Guest response coverage is at ${coverage}%`,
        description: `${scopeLabel}: coverage for ${periodLabel} (${compareMeta.short}). Staff after-hours coverage before scaling paid.`,
      }
    }
    if (index === 1) {
      const roas = Number((2.9 * Math.min(1.12, 0.94 + scopeScale * 0.1)).toFixed(1))
      return {
        ...decision,
        title: `ROAS at ${roas}x — ${roas >= 2.8 ? 'above' : 'below'} the 2.8x plan`,
        description: `Keep creative quality high for ${scopeLabel}; review efficiency ${compareMeta.label}.`,
      }
    }
    return {
      ...decision,
      description: `${scopeLabel} · ${periodLabel}: ${decision.description}`,
    }
  })
}

const buildDashboardData = (filters) => {
  const { periodScale, periodLabel } = resolveDashboardPeriodMeta(filters)
  const scopeScale = getScopeScale(filters)
  const compareMeta = getCompareMeta(filters.comparePeriod)
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const propertyIds = getPropertyIds(filters)
  const brandLabel = formatMultiSelectLabel(brandIds, BRANDS, 'All categories', 'categories')
  const hotelBrandLabel = formatMultiSelectLabel(hotelBrandIds, HOTEL_BRANDS, 'All brands', 'brands')
  const propertyList = getPropertiesForScope(brandIds, hotelBrandIds, {
    propertiesByBrand: PROPERTIES_BY_BRAND,
  })
  const propertyLabel = formatMultiSelectLabel(propertyIds, propertyList, 'All properties', 'properties')
  const scopeLabel =
    [brandLabel, hotelBrandLabel, propertyLabel].filter(Boolean).join(' • ') ||
    'All categories • All brands • All properties'
  const volumeScale = periodScale * scopeScale

  const base = {
    leadershipBrief: buildLeadershipBrief(filters, periodScale, scopeScale, compareMeta),
    demandEngagement: buildDemandEngagement(filters, periodScale, scopeScale, compareMeta),
    topCampaigns: [],
    channelMix: buildChannelMix(),
    conversationTopics: buildConversationTopics(),
    competitiveBenchmarking: buildCompetitiveBenchmarking({
      brandIds,
      brandLabels: getSelectedBrandLabels(filters),
      propertyLabels: getSelectedPropertyLabels(filters),
      propertyIds,
      scopeLabel: scopeLabel.replace(/ • /g, ' · '),
      periodScale,
      scopeScale,
      compareMeta,
      clientId: resolveExecutiveClientId(),
    }),
    supportingInsights: buildSupportingInsights(filters, periodScale, scopeScale, compareMeta),
    leadershipBriefDecisions: buildLeadershipBriefDecisions(
      filters,
      periodScale,
      scopeScale,
      compareMeta,
      scopeLabel
    ),
    scopeLabel,
    periodLabel,
    compareLabel: compareMeta.label,
    compareShort: compareMeta.short,
  }

  return base
}

const PORTFOLIO_FOLLOWERS_MILLIONS = 22.4
const PORTFOLIO_FOLLOWER_GROWTH = '+3.5%'

const formatCompactFollowers = (count) => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  }
  if (count >= 10_000) return `${Math.round(count / 1000)}K`
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return String(Math.round(count))
}

export function getExecutiveAccountContext(filters = DEFAULT_EXECUTIVE_FILTERS, options = {}) {
  const brandIds = getBrandIds(filters)
  const hotelBrandIds = getHotelBrandIds(filters)
  const propertyIds = getPropertyIds(filters)
  const singlePropertyId = !isAllFilterIds(propertyIds) && propertyIds.length === 1 ? propertyIds[0] : null

  if (singlePropertyId) {
    const hash = hashPropertyId(singlePropertyId)
    const averageFollowers =
      (PORTFOLIO_FOLLOWERS_MILLIONS * 1_000_000) / EXECUTIVE_PORTFOLIO_PROPERTY_COUNT
    const weight = 0.55 + (hash % 186) / 100
    const periodScale = resolveExecutivePeriodScale(filters.period, filters.customDateRange)
    const growth = (1.6 + (hash % 48) / 10) * Math.max(0.75, Math.min(1.15, periodScale))
    const growthDisplay = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`

    return {
      accountName: options.propertyLabel || options.accountName || singlePropertyId,
      propertyCount: 1,
      followers: formatCompactFollowers(Math.round(averageFollowers * weight)),
      followerGrowth: growthDisplay,
    }
  }

  const scopeScale = getScopeScale(filters)
  const scopedProperties = getPropertiesForScope(brandIds, hotelBrandIds, {
    propertiesByBrand: PROPERTIES_BY_BRAND,
  }).filter((property) => property.id !== 'all')
  const propertyCount =
    isAllFilterIds(brandIds) && isAllFilterIds(hotelBrandIds)
      ? EXECUTIVE_PORTFOLIO_PROPERTY_COUNT
      : Math.max(1, scopedProperties.length)
  const followers = Number((PORTFOLIO_FOLLOWERS_MILLIONS * scopeScale).toFixed(1))

  return {
    accountName: options.accountName || 'Highgate',
    propertyCount,
    followers: `${followers}M`,
    followerGrowth: scaleNumericString(PORTFOLIO_FOLLOWER_GROWTH, Math.max(0.6, scopeScale), 1),
  }
}

export function getExecutiveDashboardMock(filters = DEFAULT_EXECUTIVE_FILTERS) {
  const dashboardData = buildDashboardData(filters)

  return {
    accountContext: getExecutiveAccountContext(filters),
    brands: BRANDS,
    hotelBrands: HOTEL_BRANDS,
    propertiesByBrand: PROPERTIES_BY_BRAND,
    periodLabels: PERIOD_LABELS,
    askSoho: EXECUTIVE_ASK_SOHO,
    ...dashboardData,
  }
}

export function getPropertiesForBrands(brandIds, mock) {
  const normalized = normalizeFilterIds(brandIds)
  const byId = new Map([['all', { id: 'all', label: 'All properties' }]])

  const brandKeys = isAllFilterIds(normalized)
    ? Object.keys(mock.propertiesByBrand || {}).filter((key) => key !== 'all')
    : normalized

  brandKeys.forEach((brandId) => {
    const list = mock.propertiesByBrand[brandId] || []
    list.forEach((property) => {
      if (property.id !== 'all' && !byId.has(property.id)) {
        byId.set(property.id, property)
      }
    })
  })

  if (byId.size === 1 && mock.propertiesByBrand?.all) {
    mock.propertiesByBrand.all.forEach((property) => {
      if (property.id !== 'all' && !byId.has(property.id)) {
        byId.set(property.id, property)
      }
    })
  }

  return Array.from(byId.values())
}

export function getPropertiesForScope(categoryIds, hotelBrandIds, mock) {
  const properties = getPropertiesForBrands(categoryIds, mock)
  const normalizedHotelBrands = normalizeFilterIds(hotelBrandIds)
  if (isAllFilterIds(normalizedHotelBrands)) return properties

  return properties.filter(
    (property) =>
      property.id === 'all' || propertyMatchesHotelBrands(property.id, normalizedHotelBrands)
  )
}

export function getHotelBrandsForCategories(categoryIds, mock) {
  const properties = getPropertiesForBrands(categoryIds, mock)
  const present = new Set(
    properties.map((property) => PROPERTY_HOTEL_BRAND[property.id]).filter(Boolean)
  )

  return [
    { id: 'all', label: 'All brands' },
    ...HOTEL_BRANDS.filter((brand) => brand.id !== 'all' && present.has(brand.id)),
  ]
}

/** @deprecated Use getPropertiesForBrands */
export function getPropertiesForBrand(brandId, mock) {
  return getPropertiesForBrands([brandId], mock)
}
